import React, { useState, useEffect } from 'react';
import { ApiResponse, MonitoredProduct } from '../types';
import ProductDetailView from './ProductDetailView';
import { getChinaDate } from '../utils/time';

interface ProductStats {
  today_sales: number;
  today_reviews: number;
  total_records: number;
}

interface ProductWithStats extends MonitoredProduct {
  stats?: ProductStats;
  latest_sales?: number;
  latest_reviews?: number;
  latest_rating?: number;
}

/**
 * 监控产品列表组件
 * 以产品为主体展示监控项目
 */
const MonitoredProductsList: React.FC = () => {
  const [products, setProducts] = useState<ProductWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupByShop, setGroupByShop] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithStats | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [batchUpdating, setBatchUpdating] = useState(false); // 新增：批量更新状态
  const [deleteConfirm, setDeleteConfirm] = useState<{productId: string, productName: string} | null>(null);

  // 复制产品详情页的增量计算逻辑
  const calculateGlobalIncrements = (allRecords: any[]) => {
    if (allRecords.length === 0) return [];

    // 按时间正序排列（最早的在前），用于计算增量
    const sortedRecords = [...allRecords].reverse();
    const recordsWithIncrements = [];

    for (let i = 0; i < sortedRecords.length; i++) {
      const current = sortedRecords[i];
      const previous = i > 0 ? sortedRecords[i - 1] : null;

      // 计算相对于前一次记录的增量
      let sales_increment = 0;
      let review_increment = 0;
      
      if (i === 0) {
        // 第一条记录（最早的记录）作为基准数据，增量为0
        sales_increment = 0;
        review_increment = 0;
      } else if (previous) {
        // 有前一条记录，计算真正的增量
        sales_increment = Math.max(0, current.sales_count - previous.sales_count);
        review_increment = Math.max(0, current.review_count - previous.review_count);
      }

      recordsWithIncrements.push({
        ...current,
        sales_increment,
        review_increment
      });
    }

    // 恢复原始顺序（最新的在前）
    return recordsWithIncrements.reverse();
  };

  // 按日期分组记录并应用全局增量计算
  const groupRecordsByDate = (records: any[]) => {
    // 先进行全局增量计算
    const recordsWithIncrements = calculateGlobalIncrements(records);
    
    // 再按日期分组
    const groups = new Map<string, any[]>();
    
    for (const record of recordsWithIncrements) {
      // 直接从时间字符串提取日期部分，避免时区转换问题
      const date = record.created_at.split(' ')[0]; // 格式: "2025-06-18 17:02:58" -> "2025-06-18"
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)!.push(record);
    }
    
    // 按日期排序（最新的在前）并对每日记录按时间排序
    const sortedGroups = Array.from(groups.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, records]) => [
        date,
        records.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      ]);

    const result = new Map(sortedGroups as [string, any[]][]);
    return result;
  };

  // 获取监控产品列表
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/monitor/products');
      const result: ApiResponse<ProductWithStats[]> = await response.json();
      
      if (result.success && result.data) {
        // 为每个产品获取最新统计数据
        const productsWithStats = await Promise.all(
          result.data.map(async (product) => {
            try {
              // 获取产品完整统计信息（与产品详情页使用相同的API）
              const statsResponse = await fetch(`/api/monitor/products/${product.product_id}/stats?days=7`);
              const statsResult: ApiResponse<any> = await statsResponse.json();
              
              // 获取历史记录用于前端计算今日增量（与产品详情页逻辑一致）
              const recordsResponse = await fetch(`/api/monitor/products/${product.product_id}/records?limit=100`);
              const recordsResult: ApiResponse<any[]> = await recordsResponse.json();
              
              const fullStats = statsResult.data;
              const allRecords = recordsResult.data || [];
              
              // 使用与产品详情页相同的前端计算逻辑
              let frontendTodayStats = { sales: 0, reviews: 0 };
              
              if (allRecords.length > 0) {
                // 复制产品详情页的增量计算逻辑
                const recordsWithIncrements = calculateGlobalIncrements(allRecords);
                const groupedData = groupRecordsByDate(recordsWithIncrements);
                const today = getChinaDate();
                const todayRecords = groupedData.get(today) || [];
                
                frontendTodayStats = {
                  sales: todayRecords.reduce((sum, r) => sum + r.sales_increment, 0),
                  reviews: todayRecords.reduce((sum, r) => sum + r.review_increment, 0)
                };
              }
              
              return {
                ...product,
                stats: {
                  today_sales: frontendTodayStats.sales, // 使用前端计算的结果
                  today_reviews: frontendTodayStats.reviews, // 使用前端计算的结果
                  total_records: fullStats?.total_records || 0
                },
                latest_sales: fullStats?.latest_sales || 0,
                latest_reviews: fullStats?.latest_reviews || 0,
                latest_rating: fullStats?.latest_rating || 0
              };
            } catch (error) {
              console.error(`获取产品统计失败 ${product.product_id}: ${(error as Error).message}`);
              return product;
            }
          })
        );
        
        // 按今日销量增量倒序排列（销量增量高的在前面）
        const sortedProducts = productsWithStats.sort((a, b) => {
          const aSales = a.stats?.today_sales || 0;
          const bSales = b.stats?.today_sales || 0;
          
          // 倒序排列：b - a（大的在前）
          if (bSales !== aSales) {
            return bSales - aSales;
          }
          
          // 如果销量增量相同，则按评论增量倒序
          const aReviews = a.stats?.today_reviews || 0;
          const bReviews = b.stats?.today_reviews || 0;
          if (bReviews !== aReviews) {
            return bReviews - aReviews;
          }
          
          // 如果都相同，则按总销量倒序
          const aLatestSales = a.latest_sales || 0;
          const bLatestSales = b.latest_sales || 0;
          return bLatestSales - aLatestSales;
        });
        
        setProducts(sortedProducts);
      }
    } catch (error) {
              console.error('获取监控产品列表失败:', (error as Error).message);
    } finally {
      setLoading(false); // 重置加载状态
    }
  };

  // 批量更新所有产品监控数据
  const handleBatchUpdate = async () => {
    if (products.length === 0) {
      return;
    }

    const confirmed = confirm(`确定要批量更新 ${products.length} 个产品的监控数据吗？这可能需要一些时间。`);
    if (!confirmed) return;

    try {
      setBatchUpdating(true); // 使用独立的批量更新状态
      
      // 显示开始提示
      const startToast = document.createElement('div');
      startToast.className = 'toast toast-top toast-end';
      startToast.innerHTML = `
        <div class="alert alert-info">
          <span>🚀 开始批量更新 ${products.length} 个产品...</span>
        </div>
      `;
      document.body.appendChild(startToast);
      setTimeout(() => {
        if (document.body.contains(startToast)) {
          document.body.removeChild(startToast);
        }
      }, 3000);

      // 批量执行监控更新
      const updatePromises = products.map(async (product) => {
        try {
          const response = await fetch(`/api/monitor/products/${product.product_id}/monitor`, {
            method: 'POST',
          });
          const result: ApiResponse = await response.json();
          return { productId: product.product_id, success: result.success, message: result.message };
        } catch (error) {
          return { 
            productId: product.product_id, 
            success: false, 
            message: (error as Error).message 
          };
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.length - successCount;

      // 显示结果提示
      const resultToast = document.createElement('div');
      resultToast.className = 'toast toast-top toast-end';
      
      if (failureCount === 0) {
        resultToast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 批量更新完成！成功更新 ${successCount} 个产品</span>
          </div>
        `;
      } else {
        resultToast.innerHTML = `
          <div class="alert alert-warning">
            <span>⚠️ 批量更新完成！成功 ${successCount} 个，失败 ${failureCount} 个</span>
          </div>
        `;
      }
      
      document.body.appendChild(resultToast);
      setTimeout(() => {
        if (document.body.contains(resultToast)) {
          document.body.removeChild(resultToast);
        }
      }, 5000);

      // 重新获取列表
      await fetchProducts();

    } catch (error) {
              console.error('批量更新失败:', (error as Error).message);
      
      const errorToast = document.createElement('div');
      errorToast.className = 'toast toast-top toast-end';
      errorToast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 批量更新失败: ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 5000);
    } finally {
      setBatchUpdating(false); // 重置批量更新状态
    }
  };

  // 批量优化所有产品数据
  const handleBatchOptimize = async () => {
    if (products.length === 0) {
      return;
    }

    const confirmed = confirm(`确定要批量优化 ${products.length} 个产品的数据吗？这将删除无效的监控记录。`);
    if (!confirmed) return;

    try {
      setOptimizing(true);
      
      // 显示开始提示
      const startToast = document.createElement('div');
      startToast.className = 'toast toast-top toast-end';
      startToast.innerHTML = `
        <div class="alert alert-info">
          <span>🔧 开始批量优化 ${products.length} 个产品的数据...</span>
        </div>
      `;
      document.body.appendChild(startToast);
      setTimeout(() => {
        if (document.body.contains(startToast)) {
          document.body.removeChild(startToast);
        }
      }, 3000);

      // 批量执行数据优化
      const optimizePromises = products.map(async (product) => {
        try {
          const response = await fetch(`/api/monitor/products/${product.product_id}/optimize`, {
            method: 'POST',
          });
          const result: ApiResponse<any> = await response.json();
          return { 
            productId: product.product_id, 
            success: result.success, 
            message: result.message,
            data: result.data 
          };
        } catch (error) {
          return { 
            productId: product.product_id, 
            success: false, 
            message: (error as Error).message 
          };
        }
      });

      const results = await Promise.all(optimizePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.length - successCount;
      
      // 计算总计删除的记录数
      const totalDeletedCount = results
        .filter(r => r.success && r.data)
        .reduce((sum, r) => sum + (r.data.deletedCount || 0), 0);

      // 显示结果提示
      const resultToast = document.createElement('div');
      resultToast.className = 'toast toast-top toast-end';
      
      if (failureCount === 0) {
        resultToast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 批量优化完成！优化了 ${successCount} 个产品，删除了 ${totalDeletedCount} 条无效记录</span>
          </div>
        `;
      } else {
        resultToast.innerHTML = `
          <div class="alert alert-warning">
            <span>⚠️ 批量优化完成！成功 ${successCount} 个，失败 ${failureCount} 个，删除了 ${totalDeletedCount} 条无效记录</span>
          </div>
        `;
      }
      
      document.body.appendChild(resultToast);
      setTimeout(() => {
        if (document.body.contains(resultToast)) {
          document.body.removeChild(resultToast);
        }
      }, 5000);

      // 重新获取列表
      await fetchProducts();

    } catch (error) {
              console.error('批量优化失败:', (error as Error).message);
      
      const errorToast = document.createElement('div');
      errorToast.className = 'toast toast-top toast-end';
      errorToast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 批量优化失败: ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 5000);
    } finally {
      setOptimizing(false);
    }
  };

  // 删除监控产品
  const handleRemoveProduct = async (productId: string) => {
    const product = products.find(p => p.product_id === productId);
    if (!product) return;
    
    // 显示确认对话框
    setDeleteConfirm({
      productId,
      productName: product.product_title || `产品 ${productId}`
    });
  };

  // 确认删除产品
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const { productId } = deleteConfirm;
    setDeleteConfirm(null);

    try {
      const response = await fetch(`/api/monitor/products/${productId}`, {
        method: 'DELETE',
      });

      const result: ApiResponse = await response.json();

      if (result.success) {
        // 显示成功提示
        const toast = document.createElement('div');
        toast.className = 'toast toast-top toast-end';
        toast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 产品监控已删除</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);

        // 重新获取列表
        fetchProducts();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
              console.error('删除产品失败:', (error as Error).message);
      
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 删除失败: ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    }
  };

  // 取消删除
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // 手动监控产品
  const handleMonitorProduct = async (productId: string) => {
    try {
      const response = await fetch(`/api/monitor/products/${productId}/monitor`, {
        method: 'POST',
      });

      const result: ApiResponse = await response.json();

      if (result.success) {
        // 显示成功提示
        const toast = document.createElement('div');
        toast.className = 'toast toast-top toast-end';
        toast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 监控执行成功</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);

        // 重新获取列表
        fetchProducts();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
              console.error('执行监控失败:', (error as Error).message);
      
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 监控失败: ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    }
  };

  // 查看详情
  const handleViewDetails = (productId: string) => {
    const product = products.find(p => p.product_id === productId);
    if (product) {
      setSelectedProduct(product);
      setSelectedProductId(productId);
    }
  };

  // 关闭详情模态框
  const handleCloseDetails = () => {
    setSelectedProductId(null);
    setSelectedProduct(null);
  };

  // 格式化数字显示
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // 按店铺分组
  const groupedProducts = groupByShop 
    ? products.reduce((groups, product) => {
        const shopName = product.shop_name;
        if (!groups[shopName]) {
          groups[shopName] = [];
        }
        groups[shopName].push(product);
        return groups;
      }, {} as Record<string, ProductWithStats[]>)
    : { '所有产品': products };

  // 对分组内的产品也进行排序
  if (groupByShop) {
    Object.keys(groupedProducts).forEach(shopName => {
      groupedProducts[shopName] = groupedProducts[shopName].sort((a, b) => {
        const aSales = a.stats?.today_sales || 0;
        const bSales = b.stats?.today_sales || 0;
        
        // 倒序排列：b - a（大的在前）
        if (bSales !== aSales) {
          return bSales - aSales;
        }
        
        // 如果销量增量相同，则按评论增量倒序
        const aReviews = a.stats?.today_reviews || 0;
        const bReviews = b.stats?.today_reviews || 0;
        if (bReviews !== aReviews) {
          return bReviews - aReviews;
        }
        
        // 如果都相同，则按总销量倒序
        const aLatestSales = a.latest_sales || 0;
        const bLatestSales = b.latest_sales || 0;
        return bLatestSales - aLatestSales;
      });
    });
  }

  useEffect(() => {
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title">
            <span className="text-primary">📊</span>
            监控产品列表
          </h3>
          <div className="flex justify-center items-center h-32">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <span className="ml-4 text-base-content/60">正在加载产品列表...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 批量操作进度条 */}
      {(batchUpdating || optimizing) && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1 w-full bg-gradient-to-r from-primary/20 via-primary to-primary/20 animate-pulse"></div>
        </div>
      )}

      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          {/* 头部 */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="card-title">
              <span className="text-primary">📊</span>
              监控产品列表
              <div className="badge badge-primary badge-sm ml-2">{products.length}</div>
            </h3>
            
            <div className="flex items-center gap-3">
              <div className="form-control">
                <label className="label cursor-pointer gap-2">
                  <span className="label-text text-sm">按店铺分组</span>
                  <input 
                    type="checkbox" 
                    className="toggle toggle-primary toggle-sm" 
                    checked={groupByShop}
                    onChange={(e) => setGroupByShop(e.target.checked)}
                  />
                </label>
              </div>
              
              <button 
                className="btn btn-warning btn-outline btn-sm gap-1"
                onClick={handleBatchOptimize}
                disabled={loading || optimizing || batchUpdating || products.length === 0}
                title="批量优化所有产品的监控数据，删除无效记录"
              >
                {optimizing ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    <span className="hidden sm:inline">优化中...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                    <span className="hidden sm:inline">批量优化数据</span>
                  </>
                )}
              </button>
              
              <button 
                className="btn btn-primary btn-sm gap-1"
                onClick={handleBatchUpdate}
                disabled={loading || optimizing || batchUpdating || products.length === 0}
                title="批量更新所有产品的监控数据"
              >
                {batchUpdating ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    <span className="hidden sm:inline">更新中...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="hidden sm:inline">批量更新</span>
                  </>
                )}
              </button>
              
              <button 
                className="btn btn-ghost btn-sm"
                onClick={fetchProducts}
                disabled={loading || batchUpdating || optimizing}
                title="刷新列表"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* 产品列表 */}
          {products.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl font-bold text-base-content mb-2">暂无监控产品</h3>
              <p className="text-base-content/60">添加产品开始监控销售数据</p>
            </div>
          ) : groupByShop ? (
            // 按店铺分组视图
            <div className="space-y-8">
              {Object.entries(groupedProducts).map(([shopName, shopProducts]) => (
                <div key={shopName} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="avatar">
                      <div className="w-10 h-10 rounded-full">
                        <img 
                          src={shopProducts[0].shop_logo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjRERENkZFIi8+CjxwYXRoIGQ9Ik01MCAyNUM0MS43IDI1IDM1IDMxLjcgMzUgNDBDMzUgNDguMyA0MS43IDU1IDUwIDU1QzU4LjMgNTUgNjUgNDguMyA2NSA0MEM2NSAzMS43IDU4LjMgMjUgNTAgMjVaTTUwIDc1QzQyLjUgNzUgMzUuNjIgNzEuMjUgMzEuMjUgNjUuNjJDMzEuMjUgNTkuMzcgNDMuNzUgNTYuMjUgNTAgNTYuMjVDNTYuMjUgNTYuMjUgNjguNzUgNTkuMzcgNjguNzUgNjUuNjJDNjQuMzggNzEuMjUgNTcuNSA3NSA1MCA3NVoiIGZpbGw9IiM4QjVDRjYiLz4KPC9zdmc+'} 
                          alt={shopName}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik01MCAyNUM0MS43IDI1IDM1IDMxLjcgMzUgNDBDMzUgNDguMyA0MS43IDU1IDUwIDU1QzU4LjMgNTUgNjUgNDguMyA2NSA0MEM2NSAzMS43IDU4LjMgMjUgNTAgMjVaTTUwIDc1QzQyLjUgNzUgMzUuNjIgNzEuMjUgMzEuMjUgNjUuNjJDMzEuMjUgNTkuMzcgNDMuNzUgNTYuMjUgNTAgNTYuMjVDNTYuMjUgNTYuMjUgNjguNzUgNTkuMzcgNjguNzUgNjUuNjJDNjQuMzggNzEuMjUgNTcuNSA3NSA1MCA3NVoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-base-content">{shopName}</h3>
                      <p className="text-sm text-base-content/60">{shopProducts.length} 个产品</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {shopProducts.map((product, index) => (
                      <ProductCard 
                        key={product.product_id} 
                        product={product} 
                        onDelete={handleRemoveProduct}
                        onManualMonitor={handleMonitorProduct}
                        onViewDetails={handleViewDetails}
                        formatNumber={formatNumber}
                        rank={index + 1}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // 列表视图 - 横向一条一条
            <div className="space-y-3">
              {products.map((product, index) => (
                <ProductCard 
                  key={product.product_id} 
                  product={product} 
                  onDelete={handleRemoveProduct}
                  onManualMonitor={handleMonitorProduct}
                  onViewDetails={handleViewDetails}
                  formatNumber={formatNumber}
                  rank={index + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 产品详情模态框 */}
      {selectedProductId && selectedProduct && (
        <ProductDetailView
          productId={selectedProductId}
          productName={selectedProduct.product_title || `产品 ${selectedProductId}`}
          productImage={selectedProduct.product_image || ''}
          shopName={selectedProduct.shop_name}
          shopLogo={selectedProduct.shop_logo || ''}
          onClose={handleCloseDetails}
        />
      )}

      {/* 删除确认模态框 */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg text-error mb-4">
              <svg className="w-6 h-6 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              确认删除产品监控
            </h3>
            
            <div className="py-4">
              <p className="text-base-content mb-4">
                您确定要删除以下产品的监控吗？
              </p>
              
              <div className="bg-base-200 rounded-lg p-4 mb-4">
                <p className="font-medium text-base-content truncate" title={deleteConfirm.productName}>
                  {deleteConfirm.productName}
                </p>
                <p className="text-sm text-base-content/60 font-mono mt-1">
                  ID: {deleteConfirm.productId}
                </p>
              </div>
              
              <div className="alert alert-warning">
                <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm">
                  <strong>注意：</strong>此操作将删除该产品的所有监控记录，包括历史数据，且无法恢复。
                </span>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                className="btn btn-ghost" 
                onClick={cancelDelete}
              >
                取消
              </button>
              <button 
                className="btn btn-error" 
                onClick={confirmDelete}
              >
                确认删除
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={cancelDelete}></div>
        </div>
      )}
    </>
  );
};

// 产品卡片组件 - 横向布局
interface ProductCardProps {
  product: ProductWithStats;
  onDelete: (productId: string) => void;
  onManualMonitor: (productId: string) => void;
  onViewDetails: (productId: string) => void;
  formatNumber: (num: number) => string;
  rank?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onDelete,
  onManualMonitor,
  onViewDetails,
  formatNumber,
  rank
}) => {
  // 判断是否为前三名
  const isTopThree = rank && rank <= 3;
  
  // 获取排名徽章样式
  const getRankBadgeStyle = (rank: number) => {
    if (rank === 1) return "badge-error text-white font-bold"; // 第一名：红色
    if (rank === 2) return "badge-warning text-white font-bold"; // 第二名：橙色
    if (rank === 3) return "badge-success text-white font-bold"; // 第三名：绿色
    if (rank <= 10) return "badge-primary text-white"; // 前十名：蓝色
    return "badge-ghost"; // 其他：灰色
  };

  // 获取卡片样式
  const getCardStyle = (rank?: number) => {
    if (rank === 1) {
      return 'bg-gradient-to-r from-red-50 via-red-25 to-red-50 border-red-300 shadow-red-100';
    }
    if (rank === 2) {
      return 'bg-gradient-to-r from-orange-50 via-orange-25 to-orange-50 border-orange-300 shadow-orange-100';
    }
    if (rank === 3) {
      return 'bg-gradient-to-r from-green-50 via-green-25 to-green-50 border-green-300 shadow-green-100';
    }
    return 'bg-base-100 border-base-300 shadow-base-200';
  };

  return (
    <div className={`card hover:shadow-xl transition-all duration-300 border-2 relative h-40 ${getCardStyle(rank)}`}>
      {/* 排名标识 - 调整位置避免被遮盖 */}
      {rank && (
        <div className="absolute -top-3 -left-3 z-20">
          <div className={`badge badge-lg ${getRankBadgeStyle(rank)} shadow-lg`}>
            #{rank}
          </div>
        </div>
      )}
      
      <div className="flex w-full h-full">
        {/* 产品图片 - 占满卡片高度的正方形 */}
        <figure className="flex-shrink-0 h-full aspect-square cursor-pointer hover:opacity-80 transition-opacity relative">
          {/* 前三名的图片边框高亮 */}
          <div className={`absolute inset-0 rounded-l-xl ${
            rank === 1 ? 'ring-2 ring-red-400' :
            rank === 2 ? 'ring-2 ring-orange-400' :
            rank === 3 ? 'ring-2 ring-green-400' : ''
          }`}></div>
          <img 
            src={product.product_image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjQ4IiB5PSI0OCIgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iNCIgZmlsbD0iIzlDQTNBRiIvPgo8c3ZnIHg9IjU2IiB5PSI1NiIgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiNGRkZGRkYiPgo8cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptLTIgMTVsLTUtNSAxLjQxLTEuNDFMMTAgMTQuMTdsNy41OS03LjU5TDE5IDhsLTkgOXoiLz4KPC9zdmc+Cjwvc3ZnPg=='} 
            alt={product.product_title}
            className="w-full h-full object-cover rounded-l-xl relative z-10"
            loading="lazy"
            onClick={() => onViewDetails(product.product_id)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRkVGMUYxIi8+CjxwYXRoIGQ9Ik02NCA0OEM1Ni4yIDQ4IDUwIDU0LjIgNTAgNjJTNTYuMiA3NiA2NCA3NlM3OCA2OS44IDc4IDYyUzcxLjggNDggNjQgNDhaIiBmaWxsPSIjRUY0NDQ0Ii8+CjxwYXRoIGQ9Ik01NiA1Nkw3MiA3Mk01NiA3Mkw3MiA1NiIgc3Ryb2tlPSIjRkZGRkZGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4=';
            }}
          />
        </figure>

        {/* 产品信息 */}
        <div className="flex-1 p-4 min-w-0">
          {/* 产品基本信息 */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 min-w-0 space-y-2">
              {/* 产品名称 */}
              <div className="flex-1 min-w-0">
                <h3 
                  className={`text-base font-semibold truncate leading-tight cursor-pointer hover:text-primary transition-colors ${
                    isTopThree ? 'text-base-content' : 'text-base-content'
                  }`}
                  title={product.product_title || `产品 ${product.product_id}`}
                  onClick={() => onViewDetails(product.product_id)}
                >
                  {product.product_title || `产品 ${product.product_id}`}
                </h3>
                <p className="text-xs text-base-content/50 font-mono truncate">ID: {product.product_id}</p>
              </div>

              {/* 店铺信息 */}
              <div className="flex items-center gap-2">
                <div className="avatar">
                  <div className="w-6 h-6 rounded-full">
                    <img 
                      src={product.shop_logo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTIiIGZpbGw9IiNEREQ2RkUiLz4KPHBhdGggZD0iTTEyIDZDOS43OSA2IDggNy43OSA4IDEwQzggMTIuMjEgOS43OSAxNCAxMiAxNEMxNC4yMSAxNCAxNiAxMi4yMSAxNiAxMEMxNiA3Ljc5IDE0LjIxIDYgMTIgNlpNMTIgMThDOS44NyAxOCA4LjAxIDE2Ljc1IDcuMzEgMTQuOTdDNy4zMSAxMy43MyA5Ljc2IDEyLjUgMTIgMTIuNUMxNC4yNCAxMi41IDE2LjY5IDEzLjczIDE2LjY5IDE0Ljk3QzE1Ljk5IDE2Ljc1IDE0LjEzIDE4IDEyIDE4WiIgZmlsbD0iIzhCNUNGNiIvPgo8L3N2Zz4K'} 
                      alt={product.shop_name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTIiIGZpbGw9IiNGM0Y0RjYiLz4KPHBhdGggZD0iTTEyIDZDOS43OSA2IDggNy43OSA4IDEwQzggMTIuMjEgOS43OSAxNCAxMiAxNEMxNC4yMSAxNCAxNiAxMi4yMSAxNiAxMEMxNiA3Ljc5IDE0LjIxIDYgMTIgNlpNMTIgMThDOS44NyAxOCA4LjAxIDE2Ljc1IDcuMzEgMTQuOTdDNy4zMSAxMy43MyA5Ljc2IDEyLjUgMTIgMTIuNUMxNC4yNCAxMi41IDE2LjY5IDEzLjczIDE2LjY5IDE0Ljk3QzE1Ljk5IDE2Ljc1IDE0LjEzIDE4IDEyIDE4WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                      }}
                    />
                  </div>
                </div>
                <span className="text-sm text-base-content/70 truncate">{product.shop_name || '未知店铺'}</span>
              </div>
            </div>

            {/* 操作按钮 - 添加明显的底色 */}
            <div className="flex items-center gap-2">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => onViewDetails(product.product_id)}
              >
                查看详情
              </button>
              
              <button 
                className="btn btn-sm btn-square bg-blue-500 hover:bg-blue-600 text-white border-blue-500 hover:border-blue-600 shadow-md"
                onClick={() => onManualMonitor(product.product_id)}
                title="手动监控"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              <button 
                className="btn btn-sm btn-square bg-red-500 hover:bg-red-600 text-white border-red-500 hover:border-red-600 shadow-md"
                onClick={() => onDelete(product.product_id)}
                title="删除监控"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* 数据统计 */}
          <div className="grid grid-cols-5 gap-4 text-center">
            {/* 总销量 */}
            <div className="space-y-1">
              <div className="text-xl font-bold text-primary">
                {formatNumber(product.latest_sales || 0)}
              </div>
              <div className="text-xs text-base-content/60">总销量</div>
            </div>

            {/* 今日销量增量 - 前三名突出显示 */}
            <div className="space-y-1">
              <div className={`text-xl font-bold flex items-center justify-center gap-1 ${
                isTopThree ? 'text-green-600' : 'text-base-content/80'
              }`}>
                {isTopThree && (product.stats?.today_sales || 0) > 0 && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                )}
                +{formatNumber(product.stats?.today_sales || 0)}
              </div>
              <div className={`text-xs font-medium ${
                isTopThree ? 'text-green-600' : 'text-base-content/60'
              }`}>
                今日销量
              </div>
            </div>

            {/* 总评论 */}
            <div className="space-y-1">
              <div className="text-xl font-bold text-secondary">
                {formatNumber(product.latest_reviews || 0)}
              </div>
              <div className="text-xs text-base-content/60">总评论</div>
            </div>

            {/* 今日评论增量 */}
            <div className="space-y-1">
              <div className="text-xl font-bold text-blue-600">
                +{formatNumber(product.stats?.today_reviews || 0)}
              </div>
              <div className="text-xs text-base-content/60">今日评论</div>
            </div>

            {/* 评分 */}
            <div className="space-y-1">
              <div className="flex items-center gap-1 justify-center">
                <span className="text-yellow-500">⭐</span>
                <span className="text-xl font-bold text-accent">
                  {product.latest_rating ? product.latest_rating.toFixed(1) : '0.0'}
                </span>
              </div>
              <div className="text-xs text-base-content/60">评分</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoredProductsList; 